import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { 
    ArrowLeft, Loader2, Check, Sparkles, Scale
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIAS = [
    { id: 'propio', label: 'Propio', color: 'bg-[#5FAF9D]', desc: 'Motivación genuina' },
    { id: 'prestado_culpa', label: 'Culpa', color: 'bg-[#D07F7E]', desc: 'Para no sentirme culpable' },
    { id: 'prestado_imagen', label: 'Imagen', color: 'bg-[#E8B27A]', desc: 'Para ser visto de cierta forma' },
    { id: 'prestado_obligacion', label: 'Obligación', color: 'bg-[#7A9BA8]', desc: 'Porque "debo"' },
    { id: 'inercial', label: 'Inercia', color: 'bg-[#9AA5B1]', desc: 'Sin razón clara' }
];

export default function Modulo32DeberPrestado() {
    const [tareasOriginales, setTareasOriginales] = useState([]);
    const [clasificaciones, setClasificaciones] = useState({});
    const [reflexion1, setReflexion1] = useState('');
    const [reflexion2, setReflexion2] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);

    useEffect(() => {
        const loadData = async () => {
            try {
                // Load tasks from module 3.1 submission
                const subRes = await axios.get(`${API_URL}/api/user/submissions/inventario_frentes`, { withCredentials: true });
                if (subRes.data?.data?.tareas) {
                    setTareasOriginales(subRes.data.data.tareas.map(t => t.nombre));
                }
                // Load draft
                const draftRes = await axios.get(`${API_URL}/api/user/draft/deber_prestado`, { withCredentials: true });
                if (draftRes.data?.data) {
                    const d = draftRes.data.data;
                    if (d.clasificaciones) setClasificaciones(d.clasificaciones);
                    if (d.reflexion1) setReflexion1(d.reflexion1);
                    if (d.reflexion2) setReflexion2(d.reflexion2);
                }
                if (draftRes.data.read_only) { setReadOnly(true); setSubmittedAt(draftRes.data.submitted_at); }
            } catch { /* silent */ } finally { setLoadingData(false); }
        };
        loadData();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try {
            await axios.post(`${API_URL}/api/user/draft/save`, { module_id: 'deber_prestado', data: { clasificaciones, reflexion1, reflexion2 } }, { withCredentials: true });
        } catch { /* silent */ } finally { setSavingDraft(false); }
    }, [clasificaciones, reflexion1, reflexion2]);

    useEffect(() => {
        if (loadingData || readOnly) return;
        const timer = setTimeout(saveDraft, 2000);
        return () => clearTimeout(timer);
    }, [clasificaciones, reflexion1, reflexion2, saveDraft, loadingData, readOnly]);

    const classifiedCount = Object.keys(clasificaciones).length;
    const patronCounts = {};
    Object.values(clasificaciones).forEach(c => { patronCounts[c] = (patronCounts[c] || 0) + 1; });
    const dominante = Object.entries(patronCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
    const canSubmit = !readOnly && (classifiedCount >= 5 && reflexion1.trim().length > 0 && reflexion2.trim().length > 0);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) { toast.error('Clasifica al menos 5 tareas y completa ambas reflexiones'); return; }
        setLoading(true);
        try {
            const clsList = Object.entries(clasificaciones).map(([nombre, cls]) => ({ tarea_nombre: nombre, clasificacion: cls }));
            const { data } = await axios.post(`${API_URL}/api/user/modules/deber-prestado/submit`,
                { clasificaciones: clsList, reflexion_1: reflexion1, reflexion_2: reflexion2, patron_dominante: dominante },
                { withCredentials: true });
            setAiInsight(data.ai_insight);
            setSubmitted(true);
            toast.success('Módulo completado');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar'); }
        finally { setLoading(false); }
    };

    if (loadingData) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (submitted) {
        const domCat = CATEGORIAS.find(c => c.id === dominante);
        return (
            <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center p-4">
                <Toaster position="top-right" richColors />
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl">
                    <CardContent className="pt-8 text-center">
                        <div className={`w-20 h-20 ${domCat?.color || 'bg-[#5FAF9D]'} rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                            <Scale className="h-10 w-10 text-white" />
                        </div>
                        <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2">Mapa motivacional revelado</h2>
                        <p className="text-[#5FAF9D] font-medium mb-4">Patrón dominante: {domCat?.label || dominante}</p>
                        {aiInsight && (
                            <div className="bg-[#F5F7F8] border-l-4 border-[#5FAF9D] p-4 rounded-r-xl text-left mb-6">
                                <div className="flex items-start gap-3"><Sparkles className="h-5 w-5 text-[#5FAF9D] shrink-0 mt-0.5" /><p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p></div>
                            </div>
                        )}
                        <div className="mb-6"><ModuleFeedback moduleId="deber_prestado" chapter={3} /></div>
                    <Link to="/usuario"><Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl">Continuar</Button></Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F5F7F8]">
            <Toaster position="top-right" richColors />
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Link to="/usuario"><Button variant="ghost" size="icon" className="text-[#3E5F6E]"><ArrowLeft className="h-5 w-5" /></Button></Link>
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold text-[#5FAF9D] uppercase tracking-wide">Capítulo 3</span>
                                    <span className="text-xs text-[#7A8A93]">Módulo 3.2</span>
                                </div>
                                <h1 className="text-lg font-semibold text-[#1F2A30] font-['Outfit']">Detector de deber prestado</h1>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {savingDraft && <Loader2 className="h-3 w-3 animate-spin text-[#7A8A93]" />}
                            <span className="text-sm text-[#7A8A93]">{classifiedCount}/5 mín.</span>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="bg-white border border-[#DCE3E8] rounded-3xl shadow-sm overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-[#3E5F6E]/5 to-transparent p-6">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-[#3E5F6E] to-[#2F4A57] rounded-xl flex items-center justify-center shadow-md">
                                <Scale className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <CardTitle className="text-[#1F2A30]">¿De dónde viene cada obligación?</CardTitle>
                                <p className="text-sm text-[#4A5B63]">Clasifica cada tarea según su origen motivacional</p>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-6 space-y-6">
                        {/* Legend */}
                        <div className="flex flex-wrap gap-3">
                            {CATEGORIAS.map(cat => (
                                <div key={cat.id} className="flex items-center gap-2 text-xs">
                                    <div className={`w-3 h-3 ${cat.color} rounded-full`} />
                                    <span className="text-[#4A5B63]"><strong>{cat.label}</strong>: {cat.desc}</span>
                                </div>
                            ))}
                        </div>

                        {/* Task classifications */}
                        <div className="space-y-3">
                            {tareasOriginales.length === 0 ? (
                                <p className="text-[#7A8A93] text-center py-8">No se encontraron tareas del módulo anterior. Completa el Inventario de frentes primero.</p>
                            ) : (
                                tareasOriginales.map((tarea, idx) => (
                                    <div key={idx} className="flex items-center gap-3 p-3 bg-[#F5F7F8] rounded-xl border border-[#DCE3E8]" data-testid={`classify-task-${idx}`}>
                                        <p className="text-sm text-[#1F2A30] font-medium flex-1 min-w-0 truncate">{tarea}</p>
                                        <div className="flex gap-1.5 shrink-0">
                                            {CATEGORIAS.map(cat => (
                                                <button key={cat.id} onClick={() => setClasificaciones(prev => ({ ...prev, [tarea]: cat.id }))}
                                                    className={`px-2.5 py-1 text-xs rounded-lg transition-all ${clasificaciones[tarea] === cat.id ? `${cat.color} text-white shadow-md` : 'bg-white text-[#7A8A93] border border-[#DCE3E8] hover:border-[#3E5F6E]/30'}`}>
                                                    {cat.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Distribution summary */}
                        {classifiedCount > 0 && (
                            <div className="p-4 bg-[#F5F7F8] rounded-xl border border-[#DCE3E8]">
                                <p className="text-xs font-semibold text-[#3E5F6E] uppercase mb-3">Distribución</p>
                                <div className="flex gap-2">
                                    {CATEGORIAS.map(cat => {
                                        const count = patronCounts[cat.id] || 0;
                                        const pct = classifiedCount > 0 ? Math.round((count / classifiedCount) * 100) : 0;
                                        return (
                                            <div key={cat.id} className="flex-1 text-center">
                                                <div className={`h-2 ${cat.color} rounded-full mb-1`} style={{ opacity: count > 0 ? 1 : 0.2 }} />
                                                <p className="text-lg font-bold text-[#1F2A30]">{pct}%</p>
                                                <p className="text-xs text-[#7A8A93]">{cat.label}</p>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Reflections */}
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold text-[#3E5F6E]">¿Qué sientes al ver cuántas tareas no son realmente tuyas?</Label>
                                <Textarea value={reflexion1} onChange={e => setReflexion1(e.target.value)}
                                    placeholder="Reflexiona sobre la carga que llevas por otros..."
                                    className="border-[#DCE3E8] bg-[#F5F7F8] focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl min-h-[80px]"
                                    data-testid="reflexion1-deber" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold text-[#3E5F6E]">¿Qué pasaría si dejaras de cargar lo que no te corresponde?</Label>
                                <Textarea value={reflexion2} onChange={e => setReflexion2(e.target.value)}
                                    placeholder="Imagina la liberación de soltar lo prestado..."
                                    className="border-[#DCE3E8] bg-[#F5F7F8] focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl min-h-[80px]"
                                    data-testid="reflexion2-deber" />
                            </div>
                        </div>

                        <div className="flex justify-end pt-6 border-t border-[#DCE3E8]">
                            <Button onClick={handleSubmit} disabled={!canSubmit || loading}
                                className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl shadow-md"
                                data-testid="submit-deber">
                                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Procesando...</> : <><Check className="h-4 w-4 mr-2" />Completar módulo</>}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
